import React, { useMemo } from 'react';

import { generateStoreHoursTimetable, MenuMoreInfo } from '@order/components/menu';

import { Modal } from '../../../Modal';
import {
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentMoreInfoModal = (props: MenuContentMoreInfoModalProps) => {
  const { isVisible, onContactUs, onClose, orderChannel, restaurant, locationName } = props;

  const {
    phone,
    hours,
    address,
    city,
    state,
    zipCode,
    sanitaryGradeUrl,
    sanitaryGradeDisplayText,
  } = restaurant;

  const { permanent: permanentHours, store: storeHours } = hours;

  // ─── Derived Data ────────────────────────────────────────────────────

  const { storeOperatingHoursTimetable, permanentOperatingHoursTimetable } = useMemo(() => {
    return generateStoreHoursTimetable(permanentHours, storeHours);
  }, [permanentHours, storeHours]);

  const hasTemporaryHours = storeOperatingHoursTimetable && storeOperatingHoursTimetable.length > 0;
  const hasSanitaryGrade =
    orderChannel === 'pickup' &&
    typeof sanitaryGradeUrl === 'string' &&
    typeof sanitaryGradeDisplayText === 'string' &&
    sanitaryGradeUrl.length > 0 &&
    sanitaryGradeDisplayText.length > 0;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal visible={isVisible} size="small" fitHeight onRequestClose={onClose}>
      <MenuMoreInfo.Container>
        <MenuMoreInfo.ModalHeader>
          <MenuMoreInfo.Heading>{locationName}</MenuMoreInfo.Heading>
          <MenuMoreInfo.ModalCloseButton onPress={onClose} />
        </MenuMoreInfo.ModalHeader>

        <MenuMoreInfo.ContentContainer>
          {hasTemporaryHours ? (
            <MenuMoreInfo.Timetable timetable={storeOperatingHoursTimetable} isTemporary={true} />
          ) : null}

          <MenuMoreInfo.RestaurantAddress
            shortAddress={address}
            city={city}
            state={state}
            zipCode={zipCode}
          />

          <MenuMoreInfo.Timetable
            timetable={permanentOperatingHoursTimetable}
            isTemporary={false}
          />

          {onContactUs ? <MenuMoreInfo.ContactUs onPress={onContactUs} /> : null}

          {phone && !onContactUs ? (
            <MenuMoreInfo.RestaurantPhoneNumber phoneNumber={phone} />
          ) : null}

          {hasSanitaryGrade ? (
            <MenuMoreInfo.TextLink href={sanitaryGradeUrl}>
              {sanitaryGradeDisplayText}
            </MenuMoreInfo.TextLink>
          ) : null}
        </MenuMoreInfo.ContentContainer>

        <MenuMoreInfo.ButtonsContainer>
          <MenuMoreInfo.Button.Direction restaurantAddress={restaurant} />
          <MenuMoreInfo.Button.CopyAddress restaurantAddress={restaurant} />
        </MenuMoreInfo.ButtonsContainer>
      </MenuMoreInfo.Container>
    </Modal>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentMoreInfoModalProps = {
  isVisible: boolean;
  onContactUs?: () => void;
  onClose: () => void;
  locationName: string;
  orderChannel: MenuContentOrderChannel;
  restaurant: Pick<
    MenuContentRestaurantData,
    | 'phone'
    | 'hours'
    | 'flexMessage'
    | 'address'
    | 'city'
    | 'state'
    | 'zipCode'
    | 'sanitaryGradeDisplayText'
    | 'sanitaryGradeUrl'
  >;
};
